import { renameKeysFromRESTResponseToSDKResponse } from '@wix/sdk-runtime/rename-all-nested-keys';
import { transformRESTFloatToSDKFloat } from '@wix/sdk-runtime/transformations/float';
import { transformRESTTimestampToSDKTimestamp } from '@wix/sdk-runtime/transformations/timestamp';
import { transformPaths } from '@wix/sdk-runtime/transformations/transform-paths';
import { EventDefinition, HttpClient, NonNullablePaths } from '@wix/sdk-types';
import {
  CreateGroupOptions,
  DeleteGroupResponse,
  GetGroupBySlugOptions,
  GetGroupBySlugResponse,
  GetGroupOptions,
  Group,
  GroupCoverChangedEnvelope,
  GroupCreatedEnvelope,
  GroupDeletedEnvelope,
  GroupDescriptionChangedEnvelope,
  GroupQuery,
  GroupUpdatedEnvelope,
  GroupsQueryBuilder,
  ListGroupsOptions,
  ListGroupsResponse,
  QueryGroupsOptions,
  QueryGroupsResponse,
  UpdateGroup,
  UpdateGroupOptions,
  createGroup as universalCreateGroup,
  deleteGroup as universalDeleteGroup,
  getGroup as universalGetGroup,
  getGroupBySlug as universalGetGroupBySlug,
  listGroups as universalListGroups,
  queryGroups as universalQueryGroups,
  typedQueryGroups as universalTypedQueryGroups,
  updateGroup as universalUpdateGroup,
} from './social-groups-v2-group-groups.universal.js';

export const __metadata = { PACKAGE_NAME: '@wix/groups' };

export function createGroup(httpClient: HttpClient): CreateGroupSignature {
  return (group: Group, options?: CreateGroupOptions) =>
    universalCreateGroup(
      group,
      options,
      // @ts-ignore
      { httpClient }
    );
}

interface CreateGroupSignature {
  /**
   * Creates a group.
   *
   * When a group is created, the newly created group is added to the [Groups List](https://support.wix.com/en/article/wix-groups-about-your-groups-pages#groups-group-list) page
   * of a site.
   *
   * Specify a `creatorId` to set the group's creator. The group's creator will automatically become a group admin. See [Terminology](https://dev.wix.com/docs/rest/crm/community/groups/terminology) for more information on roles.
   *
   *
   * Wix users determine who can create a group.
   * This setting can be found in [a site's Dashboard](https://www.wix.com/my-account/site-selector/?buttonText=Select%20Site&title=Select%20a%20Site&autoSelectOnSingleSite=true&actionUrl=https:%2F%2Fwww.wix.com%2Fdashboard%2F%7B%7BmetaSiteId%7D%7D%2Fwix-groups/settings?) under **Groups > General Settings > Group Creation**.
   * If set to **members with approval**, site members can create a group with the Create Group method, and the group becomes a `createRequest` with a status of `PENDING`.
   * A Wix user either approves or rejects the request to create a group.
   * If set to **all site members**, site members can create a group with the Create Group method and no approval is required.
   * If set to **only admins**, only Wix users can create a group with the Create Group method.
   * Default is set to **members with approval**.
   * @param - Group to create.
   * @param - Optional fields for group creation.
   * @returns Created group.
   */
  (group: Group, options?: CreateGroupOptions): Promise<
    NonNullablePaths<
      Group,
      | `privacyStatus`
      | `coverImage.position.x`
      | `coverImage.position.y`
      | `settings.allowedToInviteMembers`
      | `settings.allowedToApproveJoinRequests`,
      4
    >
  >;
}

export function updateGroup(httpClient: HttpClient): UpdateGroupSignature {
  return (_id: string, group: UpdateGroup, options?: UpdateGroupOptions) =>
    universalUpdateGroup(
      _id,
      group,
      options,
      // @ts-ignore
      { httpClient }
    );
}

interface UpdateGroupSignature {
  /**
   * Updates a group.
   *
   * When a public or private group's name is updated, the slug is updated to reflect the new group name.
   * Only group admins can update their group.
   *
   * > **Notes:**
   * > + When `group.privacyStatus` is updated from `PRIVATE` to `PUBLIC`, all pending group join requests are automatically approved.
   * > + When `group.privacyStatus` is updated from `PRIVATE` to `SECRET`, all pending group join requests are automatically rejected.
   * @param - Group ID.
   * @returns Updated group.
   */
  (_id: string, group: UpdateGroup, options?: UpdateGroupOptions): Promise<
    NonNullablePaths<
      Group,
      | `privacyStatus`
      | `coverImage.position.x`
      | `coverImage.position.y`
      | `settings.allowedToInviteMembers`
      | `settings.allowedToApproveJoinRequests`,
      4
    >
  >;
}

export function deleteGroup(httpClient: HttpClient): DeleteGroupSignature {
  return (groupId: string) =>
    universalDeleteGroup(
      groupId,
      // @ts-ignore
      { httpClient }
    );
}

interface DeleteGroupSignature {
  /** @param - ID of the group to delete. */
  (groupId: string): Promise<
    NonNullablePaths<
      DeleteGroupResponse,
      | `group.privacyStatus`
      | `group.coverImage.position.x`
      | `group.coverImage.position.y`
      | `group.settings.allowedToInviteMembers`
      | `group.settings.allowedToApproveJoinRequests`,
      5
    >
  >;
}

export function getGroup(httpClient: HttpClient): GetGroupSignature {
  return (groupId: string, options?: GetGroupOptions) =>
    universalGetGroup(
      groupId,
      options,
      // @ts-ignore
      { httpClient }
    );
}

interface GetGroupSignature {
  /**
   * Retrieves a group.
   *
   * For groups with `group.privacyStatus` set to `SECRET`, only group admins and group members can see the group and its content.
   * @param - ID of the group to retrieve.
   * @returns Retrieved group.
   */
  (groupId: string, options?: GetGroupOptions): Promise<
    NonNullablePaths<
      Group,
      | `privacyStatus`
      | `coverImage.position.x`
      | `coverImage.position.y`
      | `settings.allowedToInviteMembers`
      | `settings.allowedToApproveJoinRequests`,
      4
    >
  >;
}

export function getGroupBySlug(
  httpClient: HttpClient
): GetGroupBySlugSignature {
  return (slug: string, options?: GetGroupBySlugOptions) =>
    universalGetGroupBySlug(
      slug,
      options,
      // @ts-ignore
      { httpClient }
    );
}

interface GetGroupBySlugSignature {
  /**
   * Retrieves a group by slug.
   *
   * The slug is the end of a group's URL that refers to a specific group.
   * For example, if a group's URL is `https:/example.com/groups/{my-fitness-group}`, the slug is `my-fitness-group`.
   * Slugs are case-sensitive. It is generally based on the group name, but for secret groups it is an autogenerated string of characters,
   * for example, `https:/example.com/groups/{5D3yTX}`.
   *
   * For groups with `group.privacyStatus` set to `SECRET`, only group admins and group members can see the group and its content.
   * @param - Unique part of the group's URL. For example, if a group's URL is `https:/example.com/groups/{my-fitness-group}`, the slug is `my-fitness-group`. Case-sensitive.
   */
  (slug: string, options?: GetGroupBySlugOptions): Promise<
    NonNullablePaths<
      GetGroupBySlugResponse,
      | `group.privacyStatus`
      | `group.coverImage.position.x`
      | `group.coverImage.position.y`
      | `group.settings.allowedToInviteMembers`
      | `group.settings.allowedToApproveJoinRequests`,
      5
    >
  >;
}

export function listGroups(httpClient: HttpClient): ListGroupsSignature {
  return (options?: ListGroupsOptions) =>
    universalListGroups(
      options,
      // @ts-ignore
      { httpClient }
    );
}

interface ListGroupsSignature {
  /**
   * Retrieves up to 100 groups.
   *
   *
   * Default sorts by `_createdDate` in descending order. For `SECRET` groups, only group admins and group members can see a list of groups and their content.
   *
   * > **Note:** This function's parameters are positional, and must be specified in the sequence shown in the syntax below. When specifying a parameter, use `null` as a placeholder for any unspecified parameters. For example, to specify limit only, call `listGroups(paging, null)`.
   * @param - Limit and offset options.
   */
  (options?: ListGroupsOptions): Promise<
    NonNullablePaths<
      ListGroupsResponse,
      | `groups`
      | `groups.${number}.privacyStatus`
      | `groups.${number}.coverImage.position.x`
      | `groups.${number}.coverImage.position.y`
      | `groups.${number}.settings.allowedToInviteMembers`
      | `groups.${number}.settings.allowedToApproveJoinRequests`,
      6
    >
  >;
}

export function queryGroups(httpClient: HttpClient): QueryGroupsSignature {
  return (options?: QueryGroupsOptions) =>
    universalQueryGroups(
      options,
      // @ts-ignore
      { httpClient }
    );
}

interface QueryGroupsSignature {
  /**
   * Retrieves up to 100 groups, given the provided paging, filtering, and sorting.
   *
   * Supported fields for filtering:
   * - `title`
   *
   * Supported fields for sorting:
   * - `title`
   * - `createdDate`
   * - `membersCount`
   * - `recentActivityDate`
   *
   * For groups with `group.privacyStatus` set to `SECRET`, only group admins and group members can see the group and its content.
   */
  (options?: QueryGroupsOptions): GroupsQueryBuilder;
}

export function typedQueryGroups(
  httpClient: HttpClient
): TypedQueryGroupsSignature {
  return (query: GroupQuery, options?: QueryGroupsOptions) =>
    universalTypedQueryGroups(
      query,
      options,
      // @ts-ignore
      { httpClient }
    );
}

interface TypedQueryGroupsSignature {
  /** */
  (query: GroupQuery, options?: QueryGroupsOptions): Promise<
    NonNullablePaths<
      QueryGroupsResponse,
      | `groups`
      | `groups.${number}.privacyStatus`
      | `groups.${number}.coverImage.position.x`
      | `groups.${number}.coverImage.position.y`
      | `groups.${number}.settings.allowedToInviteMembers`
      | `groups.${number}.settings.allowedToApproveJoinRequests`,
      6
    >
  >;
}

export const onGroupCreated = EventDefinition(
  'wix.social_groups.v2.group_created',
  true,
  (event: GroupCreatedEnvelope) =>
    renameKeysFromRESTResponseToSDKResponse(
      transformPaths(event, [
        {
          transformFn: transformRESTTimestampToSDKTimestamp,
          paths: [
            { path: 'entity.createdDate' },
            { path: 'entity.updatedDate' },
            { path: 'entity.recentActivityDate' },
            { path: 'entity.lastActivityDate' },
            { path: 'metadata.eventTime' },
          ],
        },
        {
          transformFn: transformRESTFloatToSDKFloat,
          paths: [
            { path: 'entity.details.logoPosition.x' },
            { path: 'entity.details.logoPosition.y' },
            { path: 'entity.details.mobileLogoPosition.x' },
            { path: 'entity.details.mobileLogoPosition.y' },
            { path: 'entity.coverImage.position.x' },
            { path: 'entity.coverImage.position.y' },
            { path: 'entity.coverImage.mobilePosition.x' },
            { path: 'entity.coverImage.mobilePosition.y' },
          ],
        },
      ])
    )
)<GroupCreatedEnvelope>();
export const onGroupDeleted = EventDefinition(
  'wix.social_groups.v2.group_deleted',
  true,
  (event: GroupDeletedEnvelope) =>
    renameKeysFromRESTResponseToSDKResponse(
      transformPaths(event, [
        {
          transformFn: transformRESTTimestampToSDKTimestamp,
          paths: [
            { path: 'undefined.createdDate' },
            { path: 'undefined.updatedDate' },
            { path: 'undefined.recentActivityDate' },
            { path: 'undefined.lastActivityDate' },
            { path: 'metadata.eventTime' },
          ],
        },
        {
          transformFn: transformRESTFloatToSDKFloat,
          paths: [
            { path: 'undefined.details.logoPosition.x' },
            { path: 'undefined.details.logoPosition.y' },
            { path: 'undefined.details.mobileLogoPosition.x' },
            { path: 'undefined.details.mobileLogoPosition.y' },
            { path: 'undefined.coverImage.position.x' },
            { path: 'undefined.coverImage.position.y' },
            { path: 'undefined.coverImage.mobilePosition.x' },
            { path: 'undefined.coverImage.mobilePosition.y' },
          ],
        },
      ])
    )
)<GroupDeletedEnvelope>();
export const onGroupCoverChanged = EventDefinition(
  'wix.social_groups.v2.group_group_cover_changed',
  true,
  (event: GroupCoverChangedEnvelope) =>
    renameKeysFromRESTResponseToSDKResponse(
      transformPaths(event, [
        {
          transformFn: transformRESTTimestampToSDKTimestamp,
          paths: [{ path: 'metadata.eventTime' }],
        },
      ])
    )
)<GroupCoverChangedEnvelope>();
export const onGroupDescriptionChanged = EventDefinition(
  'wix.social_groups.v2.group_group_description_changed',
  true,
  (event: GroupDescriptionChangedEnvelope) =>
    renameKeysFromRESTResponseToSDKResponse(
      transformPaths(event, [
        {
          transformFn: transformRESTTimestampToSDKTimestamp,
          paths: [{ path: 'metadata.eventTime' }],
        },
      ])
    )
)<GroupDescriptionChangedEnvelope>();
export const onGroupUpdated = EventDefinition(
  'wix.social_groups.v2.group_updated',
  true,
  (event: GroupUpdatedEnvelope) =>
    renameKeysFromRESTResponseToSDKResponse(
      transformPaths(event, [
        {
          transformFn: transformRESTTimestampToSDKTimestamp,
          paths: [
            { path: 'entity.createdDate' },
            { path: 'entity.updatedDate' },
            { path: 'entity.recentActivityDate' },
            { path: 'entity.lastActivityDate' },
            { path: 'metadata.eventTime' },
          ],
        },
        {
          transformFn: transformRESTFloatToSDKFloat,
          paths: [
            { path: 'entity.details.logoPosition.x' },
            { path: 'entity.details.logoPosition.y' },
            { path: 'entity.details.mobileLogoPosition.x' },
            { path: 'entity.details.mobileLogoPosition.y' },
            { path: 'entity.coverImage.position.x' },
            { path: 'entity.coverImage.position.y' },
            { path: 'entity.coverImage.mobilePosition.x' },
            { path: 'entity.coverImage.mobilePosition.y' },
          ],
        },
      ])
    )
)<GroupUpdatedEnvelope>();

export {
  AccessRestriction,
  AccessRestrictionDataOneOf,
  AccountInfo,
  AccountInfoMetadata,
  ActionEvent,
  AllowPolicy,
  BMFeature,
  BMFeatures,
  BaseEventMetadata,
  ContentType,
  CoverImage,
  CreateGroupOptions,
  CreateGroupRequest,
  CreateGroupResponse,
  DeleteGroupRequest,
  DeleteGroupResponse,
  DomainEvent,
  DomainEventBodyOneOf,
  EntityCreatedEvent,
  EntityDeletedEvent,
  EntityUpdatedEvent,
  EventMetadata,
  Events,
  FeedItemPermissions,
  FeedPermissions,
  GetGroupBMFeaturesRequest,
  GetGroupBMFeaturesResponse,
  GetGroupBySlugOptions,
  GetGroupBySlugRequest,
  GetGroupBySlugResponse,
  GetGroupIdBySlugRequest,
  GetGroupIdBySlugResponse,
  GetGroupMembersGroupIdsRequest,
  GetGroupMembersGroupIdsResponse,
  GetGroupOptions,
  GetGroupRequest,
  GetGroupResponse,
  GetPrivacyRequest,
  GetPrivacyResponse,
  GlobalFeedPermissions,
  Group,
  GroupCoverChanged,
  GroupCoverChangedEnvelope,
  GroupCreatedEnvelope,
  GroupDeletedEnvelope,
  GroupDescriptionChanged,
  GroupDescriptionChangedEnvelope,
  GroupDetails,
  GroupDetailsPosition,
  GroupIntegrationsData,
  GroupPermissions,
  GroupQuerySpec,
  GroupSettings,
  GroupUpdatedEnvelope,
  GroupWithMsId,
  GroupsQueryBuilder,
  GroupsQueryResult,
  IdentificationData,
  IdentificationDataIdOneOf,
  Identity,
  IdentityType,
  Image,
  ListGroupIntegrationsDataRequest,
  ListGroupIntegrationsDataResponse,
  ListGroupsByUserIdRequest,
  ListGroupsByUserIdResponse,
  ListGroupsOptions,
  ListGroupsRequest,
  ListGroupsResponse,
  Logo,
  MembershipStatus,
  MessageEnvelope,
  OnboardingStepSettings,
  Paging,
  PagingMetadata,
  Position,
  PrivacyStatus,
  Query,
  QueryGroupsByMembershipRequest,
  QueryGroupsByMembershipResponse,
  QueryGroupsOptions,
  QueryGroupsRequest,
  QueryGroupsResponse,
  QueryJoinedGroupsRequest,
  QueryJoinedGroupsResponse,
  QueryJoinedGroupsWithMemberRequest,
  QueryJoinedGroupsWithMemberResponse,
  RestoreInfo,
  SortOrder,
  Sorting,
  StepKey,
  Type,
  UpdateGroup,
  UpdateGroupOptions,
  UpdateGroupRequest,
  UpdateGroupResponse,
  WebhookIdentityType,
  utils,
} from './social-groups-v2-group-groups.universal.js';
